// BUG-523: a tab on an old release asks for a page chunk (/assets/Page-<hash>.js) that a newer
// deploy no longer lists, or the network drops it. The import fails and the page would stay blank.
// One full reload fixes it: the fresh index.html points at chunks that exist. Two ways in:
// - Vite's `vite:preloadError` event (its preload helper fires it for a failed chunk or CSS file),
// - lazy pages wrapped with wrap(), for an import that fails outside the helper.
// A sessionStorage time stamp stops a reload loop when the chunk is really broken: within a minute
// of the last reload the error is shown instead. Blocked storage means no guard, so no reload.

export const CHUNK_RELOAD_KEY = 'helin.chunkReloadAt'
export const CHUNK_RELOAD_WINDOW_MS = 60 * 1000

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i

export function isChunkLoadError(err: unknown): boolean {
  return err instanceof Error && CHUNK_ERROR.test(err.message)
}

export interface ChunkReloadDeps {
  storage: () => Pick<Storage, 'getItem' | 'setItem'>
  now: () => number
  reload: () => void
}

export function createChunkReloader({ storage, now, reload }: ChunkReloadDeps) {
  let started = false

  /** Reloads unless one happened in the last minute; true when a reload is (already) under way. */
  function tryReload(): boolean {
    if (started) return true
    try {
      const s = storage()
      const last = Number(s.getItem(CHUNK_RELOAD_KEY))
      if (last && now() - last < CHUNK_RELOAD_WINDOW_MS) return false
      s.setItem(CHUNK_RELOAD_KEY, String(now()))
    } catch {
      return false
    }
    started = true
    reload()
    return true
  }

  return {
    tryReload,
    /** Listens for Vite's preload error; returns stop(). */
    listen(win: Pick<Window, 'addEventListener' | 'removeEventListener'>): () => void {
      // Not preventDefault(): the import still rejects, and wrap() below keeps the page waiting.
      const onError = () => void tryReload()
      win.addEventListener('vite:preloadError', onError)
      return () => win.removeEventListener('vite:preloadError', onError)
    },
    /** For React.lazy: a chunk that fails to load reloads the page; the spinner stays meanwhile. */
    wrap<T>(factory: () => Promise<T>): () => Promise<T> {
      return () =>
        factory().catch((err: unknown) => {
          if (started || (isChunkLoadError(err) && tryReload())) return new Promise<T>(() => {})
          throw err
        })
    },
  }
}
